import {
    Autocomplete,
    Button,
    Divider,
    FormControlLabel,
    Grid,
    InputAdornment,
    Skeleton,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { Controller, useForm, useFormContext } from "react-hook-form";
import { INPUT_VALIDATION, PAGINATION } from "../../constants";
import { epsgConverter, readCSVToArray } from "./utils";
import { useCallback, useEffect, useMemo, useState } from "react";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import AgricultureIcon from "@mui/icons-material/Agriculture";
import { BACKEND_ROUTES } from "../../backendRoutes";
import { FetchErrorAlert } from "../../components/FetchErrorAlert";
import { ItemCardList } from "../../components/ItemCardList";
import { MODEL_PROPTYPES } from "../../models";
import PropTypes from "prop-types";
import { parcellaireCreationSubmitImport } from "./parcellaireCreationSubmitImport";
import { patch } from "../../utils/fetchUtils";
import proj4 from "proj4";
import useSWR from "swr";
import { useSnackbar } from "../../hooks";

ParcellaireCreationFromCloverfield.propTypes = {
    map: PropTypes.any,
    parcellaireDispatch: PropTypes.func.isRequired,
    setBlueRectangleData: PropTypes.func.isRequired,
    setGeoJsonKey: PropTypes.func.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    setSelectableMetadata: PropTypes.func.isRequired,
    initialCompany: PropTypes.shape(MODEL_PROPTYPES.Company),
    initialContract: PropTypes.shape(MODEL_PROPTYPES.Contract),
    initialSite: PropTypes.shape(MODEL_PROPTYPES.Site),
    orthoimageCompany: PropTypes.shape(MODEL_PROPTYPES.Company),
    orthoimageContract: PropTypes.shape(MODEL_PROPTYPES.Contract),
    orthoimageSite: PropTypes.shape(MODEL_PROPTYPES.Site),
    hasInitialMission: PropTypes.bool,
};

export function ParcellaireCreationFromCloverfield({
    map,
    parcellaireDispatch,
    setBlueRectangleData,
    setGeoJsonKey,
    setIsOpen,
    setSelectableMetadata,
    initialCompany,
    initialContract,
    initialSite,
    orthoimageCompany,
    orthoimageContract,
    orthoimageSite,
    hasInitialMission,
}) {
    const { openSnackbar } = useSnackbar();

    const { setValue: setValueOuterForm } = useFormContext();

    const {
        control,
        watch,
        register,
        handleSubmit,
        setValue,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            company: orthoimageCompany || initialCompany || null,
            contract: orthoimageContract || initialContract || null,
            site: orthoimageSite || initialSite || null,
            csvData: null,
            blueRectangle: null,
            xSize: "",
            ySize: "",
            xSpacing: "",
            ySpacing: "",
            xAngle: 0,
            yAngle: 0,
            usedEPSG: {},
            o: [],
            x: [],
            y: [],
            reverseSizes: false,
            sizeScaling: false,
            blockName: null,
        },
        values: initialSite && {
            company: initialCompany,
            contract: initialContract,
            site: initialSite,
        },
    });

    const [
        companyWatch,
        contractWatch,
        siteWatch,
        blueRectangleWatch,
        xSizeWatch,
        ySizeWatch,
        sizeScalingWatch,
        reverseSizesWatch,
    ] = watch([
        "company",
        "contract",
        "site",
        "blueRectangle",
        "xSize",
        "ySize",
        "sizeScaling",
        "reverseSizes",
    ]);

    const { data: companies, error: companiesFetchError } = useSWR(
        `${BACKEND_ROUTES.COMPANY}?sort=name&limit=${PAGINATION.COMPANY.LIMIT.MAX}`
    );
    const { data: contracts, error: contractsFetchError } = useSWR(
        companyWatch
            ? `${BACKEND_ROUTES.CONTRACT}?sort=name&companyUuid=${companyWatch.uuid}&limit=${PAGINATION.CONTRACT.LIMIT.MAX}`
            : null
    );
    const { data: sites, error: sitesFetchError } = useSWR(
        contractWatch
            ? `${BACKEND_ROUTES.SITE}?sort=name&contractUuid=${contractWatch.uuid}`
            : null
    );

    const { data: fieldMaps, error: fieldMapsError } = useSWR(
        siteWatch
            ? `${BACKEND_ROUTES.SITE}/${siteWatch.uuid}/field-maps`
            : null,
        {
            shouldRetryOnError: false,
        }
    );

    const [downloadedFieldMaps, setDownloadedFieldMaps] = useState(new Map());
    const [selectedFieldMapId, setSelectedFieldMapId] = useState(null);
    const [showLocationGeometry, setShowLocationGeometry] = useState(false);

    const handleFieldMapClick = useMemo(
        () => async (fieldMap) => {
            // Reset Location & Geometry form values
            setValue("xSize", "");
            setValue("ySize", "");
            setValue("xSpacing", "");
            setValue("ySpacing", "");
            setValue("xAngle", 0);
            setValue("yAngle", 0);
            setValue("usedEPSG", {});
            setValue("o", []);
            setValue("x", []);
            setValue("y", []);
            setValue("sizeScaling", false);
            setValue("reverseSizes", false);
            setValue("blockName", null);

            // Check if field map is already cached with complete payload
            if (downloadedFieldMaps.has(fieldMap.id)) {
                const cachedPayload = downloadedFieldMaps.get(fieldMap.id);

                // Restore all form values from cached payload
                Object.keys(cachedPayload).forEach((key) => {
                    setValue(key, cachedPayload[key]);
                });

                setShowLocationGeometry(true);
                setSelectedFieldMapId(fieldMap.id);
                openSnackbar("Field map loaded", "success");
                return;
            }

            // Fetch CSV file URL
            const response = await fetch(
                `${BACKEND_ROUTES.EXPERIMENT}/${fieldMap.id}/file-url`
            );

            if (!response.ok) {
                openSnackbar("Failed to get URL", "error");
                return;
            }

            const data = await response.json();

            if (data.url) {
                const fileResponse = await fetch(data.url);
                if (!fileResponse.ok) {
                    openSnackbar("This file doesn't exist", "error");
                    return;
                }

                const blob = await fileResponse.blob();
                const file = new File([blob], "fieldBook.csv", {
                    type: "text/csv",
                });

                const csvData = await readCSVToArray(
                    file,
                    setValue,
                    openSnackbar,
                    false
                );
                setValue("csvData", csvData);
                setSelectedFieldMapId(fieldMap.id);
            } else {
                openSnackbar("No CSV found in experiment data", "warning");
                return;
            }

            if (siteWatch && siteWatch.experimentStatus !== 3) {
                const response = await patch(
                    `${BACKEND_ROUTES.SITE}/${siteWatch.uuid}`,
                    {
                        body: { experimentStatus: 3 },
                    }
                );
                if (!response) {
                    openSnackbar("Failed to update site status", "error");
                    return;
                }
            }

            let blueRectangle = null;
            if (fieldMap.features) {
                blueRectangle =
                    typeof fieldMap.features === "string"
                        ? JSON.parse(fieldMap.features)
                        : fieldMap.features;
                setValue("blueRectangle", blueRectangle);
                openSnackbar("Field map downloaded", "success");

                // blueRectangle will be stored in onSubmit
                setShowLocationGeometry(true);
            } else {
                openSnackbar("No features found in experiment data", "warning");
                return;
            }
        },
        [downloadedFieldMaps, setValue, openSnackbar, siteWatch]
    );

    const getItemName = useCallback(
        (fieldMap) =>
            fieldMap.features.features.find(
                (feature) => feature.id === "Polygon"
            ).properties.block_name,
        []
    );
    const getItemChipLabel = useCallback((fieldMap) => fieldMap.crop.name, []);
    const getItemDate = useCallback((fieldMap) => fieldMap.createdAt, []);

    useEffect(() => {
        if (blueRectangleWatch) {
            const defaultCoord = blueRectangleWatch.features.find(
                (feature) => feature.id === "O"
            ).geometry.coordinates;
            const xCoord = blueRectangleWatch.features.find(
                (feature) => feature.id === "X"
            ).geometry.coordinates;
            const yCoord = blueRectangleWatch.features.find(
                (feature) => feature.id === "Y"
            ).geometry.coordinates;
            const plotProperties = blueRectangleWatch.features.find(
                (feature) => feature.id === "Polygon"
            ).properties;

            const usedEPSG = epsgConverter(defaultCoord[1], defaultCoord[0]);

            proj4.defs(usedEPSG.epsgCode, usedEPSG.epsgString);

            const defaultCoordMeter = proj4(
                "WGS84",
                usedEPSG.epsgCode,
                defaultCoord
            );
            const xCoordMeter = proj4("WGS84", usedEPSG.epsgCode, xCoord);
            const yCoordMeter = proj4("WGS84", usedEPSG.epsgCode, yCoord);
            const usedAngleX = (
                ((Math.atan2(
                    xCoordMeter[1] - defaultCoordMeter[1],
                    xCoordMeter[0] - defaultCoordMeter[0]
                ) *
                    180) /
                    Math.PI +
                    360) %
                360
            ).toFixed(10);
            const usedAngleY = (
                ((Math.atan2(
                    yCoordMeter[1] - defaultCoordMeter[1],
                    yCoordMeter[0] - defaultCoordMeter[0]
                ) *
                    180) /
                    Math.PI +
                    360) %
                360
            ).toFixed(10);
            setValue("usedEPSG", usedEPSG);
            setValue("o", defaultCoord);
            setValue("x", xCoord);
            setValue("y", yCoord);
            setValue("xAngle", usedAngleX);
            setValue("yAngle", usedAngleY);
            setValue("xSize", Number(plotProperties.plot_length));
            setValue("ySize", Number(plotProperties.plot_width));
            setValue("blockName", plotProperties.block_name);
        }
    }, [blueRectangleWatch, setValue]);

    const onSubmit = (payload) => {
        if (selectedFieldMapId) {
            setDownloadedFieldMaps((prev) =>
                prev.set(selectedFieldMapId, payload)
            );
        }

        // Use the stored payload if available, otherwise use current payload
        const finalPayload =
            downloadedFieldMaps.get(selectedFieldMapId) || payload;

        parcellaireCreationSubmitImport(
            finalPayload,
            setIsOpen,
            parcellaireDispatch,
            map,
            setGeoJsonKey,
            openSnackbar,
            false,
            null,
            setSelectableMetadata
        );
        setBlueRectangleData(finalPayload.blueRectangle);
        setValueOuterForm("selectedMetadata", null);
        setShowLocationGeometry(false);
    };

    const mergedFetchError =
        contractsFetchError ?? companiesFetchError ?? sitesFetchError;

    if (mergedFetchError) return <FetchErrorAlert error={mergedFetchError} />;

    if (!companies || (hasInitialMission && !sites)) {
        return (
            <Skeleton
                animation="wave"
                variant="rectangular"
                width="100%"
                height="25em"
            />
        );
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
                <Grid size={12}>
                    <Controller
                        control={control}
                        name="company"
                        render={({
                            field: { ref, onChange, name, ...fieldProps },
                        }) => (
                            <Autocomplete
                                {...fieldProps}
                                id="company"
                                options={companies.rows}
                                isOptionEqualToValue={(option, value) =>
                                    option.uuid === value.uuid
                                }
                                getOptionLabel={(option) => option.name}
                                onChange={(_, value) => {
                                    onChange(value);
                                    reset((formValues) => ({
                                        ...formValues,
                                        contract: null,
                                        site: null,
                                    }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        inputRef={ref}
                                        name={name}
                                        label="Company"
                                    />
                                )}
                            />
                        )}
                    />
                </Grid>
                <Grid size={12}>
                    <Controller
                        control={control}
                        name="contract"
                        render={({
                            field: { ref, onChange, name, ...fieldProps },
                        }) => (
                            <Autocomplete
                                {...fieldProps}
                                id="contract"
                                disabled={!companyWatch || !contracts}
                                options={contracts?.rows ?? []}
                                isOptionEqualToValue={(option, value) =>
                                    option.uuid === value.uuid
                                }
                                getOptionLabel={(option) => option.name}
                                onChange={(_, value) => {
                                    onChange(value);
                                    reset((formValues) => ({
                                        ...formValues,
                                        site: null,
                                    }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        inputRef={ref}
                                        name={name}
                                        label="Campaign"
                                    />
                                )}
                            />
                        )}
                    />
                </Grid>
                <Grid size={12}>
                    <Controller
                        control={control}
                        name="site"
                        render={({
                            field: { ref, onChange, name, ...fieldProps },
                        }) => (
                            <Autocomplete
                                {...fieldProps}
                                id="site"
                                disabled={!contractWatch || !sites}
                                options={sites?.rows ?? []}
                                isOptionEqualToValue={(option, value) =>
                                    option.uuid === value.uuid
                                }
                                onChange={(_, value) => {
                                    onChange(value);
                                }}
                                getOptionLabel={(option) => option.name}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        inputRef={ref}
                                        name={name}
                                        label="Site"
                                    />
                                )}
                            />
                        )}
                    />
                </Grid>
                {siteWatch && (
                    <>
                        <Grid size={12}>
                            {fieldMaps?.length > 0 && (
                                <Typography>
                                    {fieldMaps?.length} available field maps for{" "}
                                    {siteWatch?.name}
                                </Typography>
                            )}
                            <Divider />
                        </Grid>
                        <Grid size={12}>
                            <ItemCardList
                                items={fieldMaps}
                                error={fieldMapsError}
                                loadedItems={downloadedFieldMaps}
                                onItemClick={handleFieldMapClick}
                                emptyStateMessage="No field maps available"
                                loadingMessage="Loading field maps..."
                                errorMessage="Failed to load field maps."
                                getItemName={getItemName}
                                getItemChipLabel={getItemChipLabel}
                                getItemDate={getItemDate}
                                showDate={true}
                                showChip={true}
                                customIcon={AgricultureIcon}
                            />
                        </Grid>
                        {showLocationGeometry && (
                            <>
                                <Grid size={12}>
                                    <Typography>Location & Geometry</Typography>
                                    <Divider />
                                </Grid>
                                {blueRectangleWatch && (
                                    <Grid
                                        container
                                        spacing={2}
                                        alignItems="center"
                                        size={12}
                                    >
                                        <Grid size={12}>
                                            <Controller
                                                control={control}
                                                name="sizeScaling"
                                                render={({
                                                    field: {
                                                        ref,
                                                        value,
                                                        ...fieldProps
                                                    },
                                                }) => (
                                                    <FormControlLabel
                                                        {...fieldProps}
                                                        id="sizeScaling"
                                                        name="sizeScaling"
                                                        control={
                                                            <Switch
                                                                inputRef={ref}
                                                            />
                                                        }
                                                        label="Use field map's plot size instead of O X Y coordinates"
                                                        labelPlacement="end"
                                                        checked={value}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        <Grid size={6}>
                                            <Controller
                                                control={control}
                                                name="reverseSizes"
                                                render={({
                                                    field: {
                                                        ref,
                                                        onChange,
                                                        name,
                                                        ...fieldProps
                                                    },
                                                }) => (
                                                    <FormControlLabel
                                                        {...fieldProps}
                                                        id="reverseSizes"
                                                        name="reverseSizes"
                                                        control={<Switch />}
                                                        label={
                                                            sizeScalingWatch
                                                                ? "Swap length and width"
                                                                : "Swap X and Y points"
                                                        }
                                                        labelPlacement="end"
                                                        checked={
                                                            reverseSizesWatch
                                                        }
                                                        onChange={(event) => {
                                                            onChange(
                                                                event.target
                                                                    .checked
                                                            );
                                                        }}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        <Grid size={6}>
                                            <Typography>
                                                Plot size:{" "}
                                                {sizeScalingWatch
                                                    ? reverseSizesWatch
                                                        ? `${ySizeWatch}m x ${xSizeWatch}m`
                                                        : `${xSizeWatch}m x ${ySizeWatch}m`
                                                    : "Based on O X Y"}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                )}
                                <Grid container spacing={2} size={12}>
                                    <Grid size={6}>
                                        <TextField
                                            type="number"
                                            label="X spacing"
                                            id="xSpacing"
                                            error={Boolean(errors.xSpacing)}
                                            helperText={
                                                errors.xSpacing?.message
                                            }
                                            {...register("xSpacing", {
                                                required:
                                                    INPUT_VALIDATION.REQUIRED,
                                                valueAsNumber: true,
                                                validate: (value) =>
                                                    value >= 0 ||
                                                    INPUT_VALIDATION.POSITIVE,
                                            })}
                                            inputProps={{
                                                step: "0.01",
                                                inputMode: "decimal",
                                            }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        m
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <TextField
                                            type="number"
                                            label="Y spacing"
                                            id="ySpacing"
                                            error={Boolean(errors.ySpacing)}
                                            helperText={
                                                errors.ySpacing?.message
                                            }
                                            {...register("ySpacing", {
                                                required:
                                                    INPUT_VALIDATION.REQUIRED,
                                                valueAsNumber: true,
                                                validate: (value) =>
                                                    value >= 0 ||
                                                    INPUT_VALIDATION.POSITIVE,
                                            })}
                                            inputProps={{
                                                step: "0.01",
                                                inputMode: "decimal",
                                            }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        m
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid
                                    container
                                    justifyContent="flex-end"
                                    size={12}
                                >
                                    <Button
                                        variant="contained"
                                        startIcon={<AddCircleIcon />}
                                        type="submit"
                                    >
                                        Create
                                    </Button>
                                </Grid>
                            </>
                        )}
                    </>
                )}
            </Grid>
        </form>
    );
}
